import {
  tableBodyClasses,
  tableCellClasses,
  tableHeadClasses,
  TableCellProps,
  TableProps,
  Theme,
} from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { ReactNode } from 'react';
import {
  SENSOR_TYPE_TO_COLOR_STOPS,
  TEMPERATURE_COLOR_STOPS,
} from 'features/weatherStations/weatherStations.constants';
import { WeatherStationObservationDataKey } from 'features/weatherStations/weatherStations.types';
import { FocusedSensorType } from 'state/useWeatherStationState';
import { ColorStop } from 'features/mapping/mapping.types';
import { getInterpolatedColor } from 'features/mapping/mapping.utils';
import { EMPTY_DATA_VALUE } from '../../../../constants';

const TABLE_CELL_HEIGHT = 32;
const TABLE_CELL_WIDTH = 48;
const TABLE_ROW_SEPARATOR_HEIGHT = 12;

const scrollShadowBase = {
  content: '""',
  position: 'absolute',
  width: 8,
  pointerEvents: 'none',
  zIndex: 1,
  transition: 'opacity 0.2s',
};

const scrollShadowBackground = (
  direction: 'left' | 'right',
  opacity: number,
): string =>
  `linear-gradient(to ${direction}, rgba(0, 0, 0, ${opacity}), transparent)`;

export const scrollShadowSx = (theme: Theme): SystemStyleObject<Theme> => ({
  '&::before': {
    ...scrollShadowBase,
    // Bottom offset avoids overlapping the scrollbar on pointer devices
    inset: `0 auto 0 ${TABLE_CELL_WIDTH}px`,
    '@media (pointer: fine)': {
      bottom: 0,
    },
    background: scrollShadowBackground('right', 0.1),
    ...theme.applyStyles('dark', {
      background: scrollShadowBackground('right', 0.3),
    }),
  } as SystemStyleObject<Theme>,
  '&::after': {
    ...scrollShadowBase,
    // Bottom offset avoids overlapping the scrollbar on pointer devices
    inset: '0 0 0 auto',
    '@media (pointer: fine)': {
      bottom: 0,
    },
    background: scrollShadowBackground('left', 0.1),
    ...theme.applyStyles('dark', {
      background: scrollShadowBackground('left', 0.3),
    }),
  } as SystemStyleObject<Theme>,
});

export const tableSx: TableProps['sx'] = {
  tableLayout: 'fixed',
  width: 'max-content',
  [`& .${tableCellClasses.root}`]: {
    borderBottom: 'none',
    padding: 0,
    height: TABLE_CELL_HEIGHT,
    textAlign: 'center',
    width: TABLE_CELL_WIDTH,
    minWidth: TABLE_CELL_WIDTH,
    '&:first-of-type': (theme: Theme) => ({
      position: 'sticky',
      left: 0,
      zIndex: 3,
      backgroundColor: theme.palette.grey[100],
      boxShadow: `inset -1px 0 0 0 ${theme.palette.divider}`,
      ...theme.applyStyles('dark', {
        backgroundColor: theme.palette.grey[900],
      }),
    }),
  },
  [`& .${tableHeadClasses.root} .${tableCellClasses.root}`]: (
    theme: Theme,
  ) => ({
    fontSize: 12,
    lineHeight: 0.9,
    fontWeight: 'fontWeightBold',
    backgroundColor: theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[900],
    }),
  }),
  [`& .${tableHeadClasses.root} .${tableCellClasses.root}:first-of-type`]: {
    borderBottom: 'none',
  },
  [`& .${tableBodyClasses.root} .${tableCellClasses.root}:first-of-type`]: {
    fontSize: 12,
    lineHeight: 0.9,
    fontWeight: 'fontWeightBold',
  },
  [`& .${tableBodyClasses.root} .focused_row .${tableCellClasses.root}:first-of-type`]:
    (theme: Theme) => ({
      backgroundColor: theme.palette.grey[300],
      ...theme.applyStyles('dark', {
        backgroundColor: theme.palette.grey[800],
      }),
    }),
  [`& .${tableBodyClasses.root} .${tableCellClasses.root}:not(:first-of-type)`]:
    (theme: Theme) => ({
      ...theme.typography.body2,
    }),
  '& .separator-row': {
    height: `${TABLE_ROW_SEPARATOR_HEIGHT}px !important`,
    [`& .${tableCellClasses.root}`]: {
      height: `${TABLE_ROW_SEPARATOR_HEIGHT}px !important`,
      minHeight: `${TABLE_ROW_SEPARATOR_HEIGHT}px !important`,
      padding: '0 !important',
    },
  },
};

const valueToColor = (value: number | null, stops: ColorStop[]): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 'transparent';

  return getInterpolatedColor(value, stops);
};

const extractNumber = (temp: string): number | null => {
  const match = temp.match(/-?\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : null;
};

const parseValue = (value: ReactNode): number | null => {
  if (typeof value !== 'string' || value === EMPTY_DATA_VALUE) {
    return null;
  }
  return extractNumber(value);
};

const valuesToLinearGradient = (
  values: number[],
  colorStops: ColorStop[],
): string | undefined => {
  const stops = values.map((value, i) => {
    const color = valueToColor(value, colorStops);
    const pos = Math.round((i / (values.length - 1)) * 100);
    return `${color} ${pos}%`;
  });

  return `linear-gradient(90deg, ${stops.join(', ')})`;
};

export const getTableCellSx = (input: {
  values: ReactNode[];
  type: WeatherStationObservationDataKey;
  value: ReactNode;
  index: number;
  focusedType: FocusedSensorType;
}): TableCellProps['sx'] => {
  const { values, type, value, index, focusedType } = input;

  if (focusedType !== type) {
    return {};
  }

  const colorStops =
    SENSOR_TYPE_TO_COLOR_STOPS[focusedType] || TEMPERATURE_COLOR_STOPS;

  const prev = values[index - 1];
  // Ensure the gradient has at least two colors by repeating `value`.
  const valuesForGradient = [prev, value, value]
    .map(parseValue)
    .filter((v) => v !== null);

  const gradient = valuesToLinearGradient(valuesForGradient, colorStops);

  if (!gradient) {
    return {};
  }

  const lastValidIndex = values.findLastIndex(
    (val) => parseValue(val) !== null,
  );
  const numericValue = parseValue(value);
  const isMarkerValueValid = index === lastValidIndex && numericValue !== null;

  // Show gradient unless all these conditions are met:
  // 1. Less than 3 valid values (current and previous only)
  // 2. No change between previous and current value
  // 3. This is the last value (where we show a marker)
  const shouldShowGradient = !(
    valuesForGradient.length < 3 &&
    valuesForGradient[0] === valuesForGradient[1] &&
    isMarkerValueValid
  );

  const baseGradientStyles = {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: gradient,
    opacity: 0.3,
    zIndex: 0,
    pointerEvents: 'none',
  };

  const markerStyles = {
    content: `"${value}"`,
    position: 'absolute',
    inset: 0,
    left: 8,
    backgroundColor: getInterpolatedColor(numericValue!, colorStops),
    opacity: 1,
    zIndex: 2,
    pointerEvents: 'none',
    width: 32,
    height: 32,
    borderRadius: '8px',
    color: (theme: Theme) =>
      theme.palette.getContrastText(
        getInterpolatedColor(numericValue!, colorStops),
      ),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 'inherit',
    fontWeight: 'bold',
  };

  return {
    position: 'relative',
    ...(shouldShowGradient && {
      '&::before': {
        ...baseGradientStyles,
        ...(isMarkerValueValid && { right: 24 }),
      },
    }),
    ...(isMarkerValueValid && { '&::after': markerStyles }),
    '& > *': {
      position: 'relative',
      zIndex: 1,
    },
  };
};
